import { useFormInputValidation } from '@/contexts/shared/form-validation/UseFormInputValidation.composables'
import { computed, ref } from 'vue'
import type {
  FormValidationOptions,
  FormInputValidation,
  FormInputValidationOptions,
} from '@/contexts/shared/form-validation/FormValidation.interfaces'
import { objectFromEntries, objectKeys } from '@/helpers/TypedObjects.helpers'
import { resolveFieldLabel, translateZodError } from '@/contexts/shared/form-validation/FormValidation.helpers'
import { z } from 'zod'

/**
 * Composable for validating multiple form inputs against Zod schemas.
 * Wraps `useFormInputValidation` for each field and exposes form-level state and actions.
 *
 * @param formValidation - Record of field definitions, each containing a `schema`, `value`, and optional `validateOn`
 * @param options - Form-level validation options (initValidation, validateOn, formSchema). If a field defines its own `validateOn`, it overrides the form-level one for that field
 *
 * @returns
 * - `valid` — Computed that is true only when all fields are valid and formSchema passes
 * - `invalid` — Computed inverse of `valid`
 * - `isDirty` — Computed that is true when at least one field has been modified
 * - `validate` — Triggers async validation on all fields and formSchema. Returns a promise that resolves when all are done
 * - `validated` — Returns a promise that resolves when all current field validations complete or are cancelled
 * - `validators` — Record of per-field `useFormInputValidation` instances, keyed by field name
 * - `formErrors` — Ref containing form-level errors (from formSchema errors without a field path)
 * - `resetValidation` — Clears errors and resets validation state on all fields and formErrors
 * - `reset` — Resets all fields to their initial values and clears validation state
 * - `clearErrors` — Clears errors on a specific field (by name) or on all fields and formErrors if no name is provided
 * - `setDirty` — Manually sets the dirty state of a specific field
 */
export const useFormValidation = <T extends Record<K, FormInputValidation>, K extends keyof T>(
  formValidation: T,
  options: FormValidationOptions = {}
) => {
  const keys = objectKeys<K>(formValidation)

  type MappedZodSchemaType<TKey extends K> = z.infer<T[TKey]['schema']>
  type Validators = {
    [P in K]: ReturnType<typeof useFormInputValidation<MappedZodSchemaType<P>>>
  }

  const validators = objectFromEntries<Validators>(
    keys.map((key: K): [K, ReturnType<typeof useFormInputValidation>] => {
      const { value, schema, validateOn: fieldValidateOn, label } = formValidation[key]
      const fieldOptions: FormInputValidationOptions = {
        ...options,
        ...(fieldValidateOn ? { validateOn: fieldValidateOn } : {}),
        label: label ?? resolveFieldLabel(String(key)),
      }
      return [key, useFormInputValidation<MappedZodSchemaType<typeof key>>(value, schema, fieldOptions)]
    })
  )

  const formErrors = ref<string[]>([])
  const _formSchemaValid = ref<boolean>(true)

  const validateFormSchema = async () => {
    if (!options.formSchema) {
      return
    }

    const formValues = objectFromEntries<Record<string, unknown>>(keys.map((key) => [key, validators[key].input.value]))

    const isFieldKey = (key: string): key is string & K => key in validators

    const parsed = await options.formSchema.safeParseAsync(formValues, {
      errorMap: (issue) => {
        const fieldPath = issue.path.length > 0 ? String(issue.path[0]) : null
        if (fieldPath !== null && isFieldKey(fieldPath)) {
          const fieldLabel = formValidation[fieldPath].label ?? resolveFieldLabel(fieldPath)
          return { message: translateZodError(issue, fieldLabel) }
        }
        return { message: translateZodError(issue) }
      },
    })

    if (parsed.success) {
      _formSchemaValid.value = true
      formErrors.value = []
      return
    }

    _formSchemaValid.value = false
    const globalErrors: string[] = []

    for (const error of parsed.error.errors) {
      const fieldPath = error.path.length > 0 ? String(error.path[0]) : null

      if (fieldPath !== null && isFieldKey(fieldPath)) {
        if (!validators[fieldPath].errors.value.includes(error.message)) {
          validators[fieldPath].errors.value = [...validators[fieldPath].errors.value, error.message]
        }
        validators[fieldPath].valid.value = false
      } else {
        globalErrors.push(error.message)
      }
    }

    formErrors.value = globalErrors
  }

  const validate = async () => {
    await Promise.all(keys.map((key) => validators[key].validate()))
    await validateFormSchema()
  }

  const resetValidation = () => {
    keys.forEach((key) => validators[key].resetValidation())
    formErrors.value = []
    _formSchemaValid.value = true
  }

  const reset = () => {
    keys.forEach((key) => validators[key].reset())
    formErrors.value = []
    _formSchemaValid.value = true
  }

  const clearErrors = (fieldName?: K) => {
    if (fieldName) {
      validators[fieldName].resetValidation()
    } else {
      resetValidation()
    }
  }

  const valid = computed(() => keys.every((key: K) => validators[key].valid.value) && _formSchemaValid.value)
  const invalid = computed(() => !valid.value)
  const isDirty = computed(() => keys.some((key: K) => validators[key].dirty.value))

  const setDirty = (fieldName: K, value: boolean) => {
    validators[fieldName].dirty.value = value
  }

  const validated = () => Promise.all(keys.map((key) => validators[key].validated()))

  return {
    valid,
    invalid,
    isDirty,
    validate,
    validated,
    validators,
    formErrors,
    resetValidation,
    reset,
    clearErrors,
    setDirty,
  }
}
